// src/features/admin-options/components/HotelSettingsTab.tsx
//
// «Opciones del hotel» (mockup 13, second tab) — HotelAdmin+ for their own
// hotels: stuck thresholds S/R/I/F/U/P/K, onlineThresholdsDoubled,
// chargesOnline, wall theme/content/footerCounters, silentDeviceHours.
// GroupAdmin gets «Aplicar a todos los hoteles del grupo» (the group of the
// selected hotel). The duration ranges are a per-user switch on Tiempos, not
// a hotel setting, so they are not offered here.
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { HomeWorkRounded as HotelSettingsIcon } from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useHotelScope } from '@/hooks/useHotelScope';
import { getCurrentUser } from '@/utils/auth';
import type { HotelSettings } from '@/types/reservationState';

/** The chain of the hotel being edited: «Aplicar a todos los hoteles del grupo» applies to it. */
export const selectedHotelGroupId = (
  hotels: ReadonlyArray<{ id: string; groupId?: string | null }>,
  hotelId: string
): string | undefined => hotels.find((h) => h.id === hotelId)?.groupId ?? undefined;

const STUCK_SECTIONS: Array<keyof HotelSettings['stuckThresholdsMinutes']> = ['S', 'R', 'I', 'F', 'U', 'P', 'K'];

export const HotelSettingsTab: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const scope = useHotelScope();
  const user = getCurrentUser();
  const isGroupAdmin = user?.role === 'GroupAdmin' || user?.role === 'SuperAdmin' || user?.role === 'Admin';

  const [hotelId, setHotelId] = useState<string>('');
  const [settings, setSettings] = useState<HotelSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!hotelId && scope.allHotels.length > 0) setHotelId(scope.allHotels[0].id);
  }, [scope.allHotels, hotelId]);

  useEffect(() => {
    if (!hotelId) return;
    (dataProvider as any).getHotelSettings(hotelId).then((res: { data: HotelSettings }) => setSettings(res.data));
  }, [dataProvider, hotelId]);

  const patch = (p: Partial<HotelSettings>) => setSettings((prev) => (prev ? { ...prev, ...p } : prev));

  const handleSave = async () => {
    if (!hotelId || !settings) return;
    setSaving(true);
    try {
      await (dataProvider as any).updateHotelSettings(hotelId, settings);
      notify(translate('options.toast.saved'), { type: 'success' });
    } catch {
      notify(translate('options.toast.saveError'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToGroup = async () => {
    if (!settings) return;
    const groupId = selectedHotelGroupId(scope.allHotels, hotelId);
    if (!groupId) return;
    if (!window.confirm(translate('options.hotel.applyToGroupConfirm'))) return;
    setSaving(true);
    try {
      await (dataProvider as any).applyHotelSettingsToGroup(groupId, settings);
      notify(translate('options.toast.saved'), { type: 'success' });
    } catch {
      notify(translate('options.toast.saveError'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <Box sx={{ maxWidth: 640 }}>
      {/* Header — icon + heading + one-line description, shared across every admin-options tab */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <HotelSettingsIcon color="primary" />
        <Typography variant="h6">{translate('options.hotel.help.title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('options.hotel.help.description')}
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            size="small"
            label={translate('options.hotel.selectHotel')}
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
          >
            {scope.allHotels.map((h) => (
              <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
            ))}
          </TextField>

          <Divider />
          <Typography variant="subtitle2">{translate('options.hotel.sections.stuck')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
            {STUCK_SECTIONS.map((code) => (
              <TextField
                key={code}
                type="number"
                size="small"
                label={translate(`options.hotel.fields.stuck${code}`)}
                value={settings.stuckThresholdsMinutes[code] ?? ''}
                onChange={(e) =>
                  patch({ stuckThresholdsMinutes: { ...settings.stuckThresholdsMinutes, [code]: Number(e.target.value) } })
                }
              />
            ))}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.onlineThresholdsDoubled}
                onChange={(e) => patch({ onlineThresholdsDoubled: e.target.checked })}
              />
            }
            label={translate('options.hotel.fields.onlineThresholdsDoubled')}
          />

          <Divider />
          <Typography variant="subtitle2">{translate('options.hotel.sections.general')}</Typography>
          <FormControlLabel
            control={<Checkbox checked={settings.chargesOnline} onChange={(e) => patch({ chargesOnline: e.target.checked })} />}
            label={translate('options.hotel.fields.chargesOnline')}
          />
          <TextField
            type="number"
            size="small"
            label={translate('options.hotel.fields.silentDeviceHours')}
            value={settings.silentDeviceHours}
            onChange={(e) => patch({ silentDeviceHours: Number(e.target.value) })}
          />

          <Divider />
          <Typography variant="subtitle2">{translate('options.hotel.sections.wall')}</Typography>
          <TextField
            select
            size="small"
            label={translate('options.hotel.fields.wallTheme')}
            value={settings.wall.theme}
            onChange={(e) => patch({ wall: { ...settings.wall, theme: e.target.value as 'dark' | 'light' } })}
          >
            <MenuItem value="dark">{translate('options.hotel.wallThemeOptions.dark')}</MenuItem>
            <MenuItem value="light">{translate('options.hotel.wallThemeOptions.light')}</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label={translate('options.hotel.fields.wallContent')}
            value={settings.wall.content}
            onChange={(e) => patch({ wall: { ...settings.wall, content: e.target.value as 'full' | 'liveOnly' } })}
          >
            <MenuItem value="full">{translate('options.hotel.wallContentOptions.full')}</MenuItem>
            <MenuItem value="liveOnly">{translate('options.hotel.wallContentOptions.liveOnly')}</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.wall.footerCounters}
                onChange={(e) => patch({ wall: { ...settings.wall, footerCounters: e.target.checked } })}
              />
            }
            label={translate('options.hotel.fields.wallFooterCounters')}
          />

          <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {translate('common.actions.save')}
            </Button>
            {isGroupAdmin && selectedHotelGroupId(scope.allHotels, hotelId) && (
              <Button variant="outlined" onClick={handleApplyToGroup} disabled={saving}>
                {translate('options.hotel.applyToGroup')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HotelSettingsTab;
